import { datadogRum } from '@datadog/browser-rum'
import { datadogLogs } from '@datadog/browser-logs'
import type { DatadogConfig, UserData } from './types'

class DatadogService {
  private initialized = false
  private config: DatadogConfig | null = null
  private logsAvailable = true

  /**
   * Initialize Datadog RUM and Logs
   */
  async init(config: DatadogConfig): Promise<void> {
    if (this.initialized) {
      console.warn('[Datadog] Already initialized')
      return
    }

    if (!config.enabled) {
      console.log('[Datadog] Disabled via configuration')
      return
    }

    // Validate required configuration
    if (!config.applicationId || !config.clientToken) {
      console.error(
        '[Datadog] Missing required configuration (applicationId or clientToken)'
      )
      return
    }

    try {
      this.config = config

      // Initialize RUM
      const rumConfig: any = {
        applicationId: config.applicationId,
        clientToken: config.clientToken,
        service: config.service,
        env: config.environment,
        version: config.version,
        sessionSampleRate: config.sessionSampleRate || 100,
        sessionReplaySampleRate: config.sessionReplaySampleRate || 20,
        trackUserInteractions: config.trackUserInteractions ?? true,
        trackResources: config.trackResources ?? true,
        trackLongTasks: config.trackLongTasks ?? true,
        defaultPrivacyLevel: config.defaultPrivacyLevel || 'mask-user-input',
        allowedTracingUrls: config.allowedTracingUrls || [],
      }
      
      if (config.site) {
        rumConfig.site = config.site
      }
      
      datadogRum.init(rumConfig)
      console.log('🔍 RUM Config:', rumConfig)

      // Initialize Logs
      try {
        const logsConfig: any = {
          clientToken: config.clientToken,
          service: config.service,
          env: config.environment,
          version: config.version,
          forwardErrorsToLogs: true,
          sessionSampleRate: config.sessionSampleRate || 100,
        }
        
        if (config.site) {
          logsConfig.site = config.site
        }
        
        datadogLogs.init(logsConfig)
      } catch (error) {
        console.warn('[Datadog] Logs initialization failed:', error)
        this.logsAvailable = false
      }

      // Start session tracking
      datadogRum.startSessionReplayRecording()

      this.initialized = true

      if (config.debug) {
        console.log('[Datadog] Successfully initialized', {
          service: config.service,
          env: config.environment,
          version: config.version,
        })
      }
    } catch (error) {
      console.error('[Datadog] Initialization failed:', error)
      throw error
    }
  }

  /**
   * Identify user for tracking
   */
  identify(userData: UserData): void {
    if (!this.initialized) {
      console.warn('[Datadog] Cannot identify user - not initialized')
      return
    }

    try {
      datadogRum.setUser({
        id: userData.id,
        email: userData.email,
        name: userData.name,
        ...userData.properties,
      })

      if (this.config?.debug) {
        console.log('[Datadog] User identified:', userData.id)
      }
    } catch (error) {
      console.error('[Datadog] Failed to identify user:', error)
    }
  }

  /**
   * Clear user identity
   */
  clearUser(): void {
    if (!this.initialized) return

    try {
      datadogRum.clearUser()

      if (this.config?.debug) {
        console.log('[Datadog] User cleared')
      }
    } catch (error) {
      console.error('[Datadog] Failed to clear user:', error)
    }
  }

  /**
   * Track custom action/event
   */
  trackEvent(eventName: string, properties?: Record<string, any>): void {
    if (!this.initialized) return

    try {
      datadogRum.addAction(eventName, properties)

      if (this.config?.debug) {
        console.log('[Datadog] Event tracked:', eventName, properties)
      }
    } catch (error) {
      console.error('[Datadog] Failed to track event:', error)
    }
  }

  /**
   * Add error to tracking
   */
  trackError(error: Error, context?: Record<string, any>): void {
    if (!this.initialized) return

    try {
      datadogRum.addError(error, context)

      if (this.config?.debug) {
        console.log('[Datadog] Error tracked:', error.message, context)
      }
    } catch (err) {
      console.error('[Datadog] Failed to track error:', err)
    }
  }

  /**
   * Log message
   */
  log(
    message: string,
    level: 'debug' | 'info' | 'warn' | 'error' = 'info',
    context?: Record<string, any>
  ): void {
    if (!this.initialized || !this.logsAvailable) return

    try {
      datadogLogs.logger[level](message, context)

      if (this.config?.debug && level === 'debug') {
        console.log('[Datadog] Log:', level, message, context)
      }
    } catch (error) {
      console.error('[Datadog] Failed to log:', error)
      this.logsAvailable = false
    }
  }

  /**
   * Add global context to all RUM events
   */
  setGlobalContext(key: string, value: any): void {
    if (!this.initialized) return

    try {
      datadogRum.setGlobalContextProperty(key, value)
    } catch (error) {
      console.error('[Datadog] Failed to set global context:', error)
    }
  }

  /**
   * Remove global context property
   */
  removeGlobalContext(key: string): void {
    if (!this.initialized) return

    try {
      datadogRum.removeGlobalContextProperty(key)
    } catch (error) {
      console.error('[Datadog] Failed to remove global context:', error)
    }
  }

  /**
   * Stop session completely (ends session and stops replay)
   */
  stopSession(): void {
    if (!this.initialized) return

    try {
      // Stop session replay recording
      datadogRum.stopSessionReplayRecording()
      
      // Stop the RUM session entirely
      datadogRum.stopSession()

      console.log('[Datadog] Session stopped completely')
    } catch (error) {
      console.error('[Datadog] Failed to stop session:', error)
    }
  }

  /**
   * Stop session replay recording only (session continues)
   */
  stopSessionReplay(): void {
    if (!this.initialized) return

    try {
      datadogRum.stopSessionReplayRecording()

      if (this.config?.debug) {
        console.log('[Datadog] Session replay recording stopped')
      }
    } catch (error) {
      console.error('[Datadog] Failed to stop session replay:', error)
    }
  }

  /**
   * Start session replay recording
   */
  startSessionReplay(): void {
    if (!this.initialized) return

    try {
      datadogRum.startSessionReplayRecording()

      if (this.config?.debug) {
        console.log('[Datadog] Session replay recording started')
      }
    } catch (error) {
      console.error('[Datadog] Failed to start session replay:', error)
    }
  }

  /**
   * Check if initialized
   */
  isInitialized(): boolean {
    return this.initialized
  }
}

// Export singleton instance
export const datadogService = new DatadogService()

